import React, { useState, useEffect, useRef } from 'react';
import { Grid, Typography, TextField, Paper, Button } from '@mui/material';
import { FaSearch, FaSmile } from 'react-icons/fa';
import { MdMic } from 'react-icons/md';
import SendIcon from '@mui/icons-material/Send';
import AccountCircleIcon from '@mui/icons-material/AccountCircle';
import EmojiPicker from 'emoji-picker-react';
import axios from 'axios';
import './style.css';

const SESSION_LIMIT = 50;

export default function WhatsAppClone() {
  const [conversations, setConversations] = useState([]);
  const [selectedConversation, setSelectedConversation] = useState(null);
  const [messages, setMessages] = useState([]);
  const [newMessage, setNewMessage] = useState('');
  const [msgSkip, setMsgSkip] = useState(0);
  const [hasMoreMessages, setHasMoreMessages] = useState(true);
  const [showEmojiPicker, setShowEmojiPicker] = useState(false);

  const chatContainerRef = useRef(null);
  const emojiPickerRef = useRef(null);

  // Fetch conversation list from /conversations
  useEffect(() => {
    axios.get('/conversations?limit=50')
      .then((res) => {
        setConversations(res.data.conversations);
        if (res.data.conversations.length > 0) {
          setSelectedConversation(res.data.conversations[0]);
        }
      })
      .catch((err) => console.error("Error fetching conversations:", err));
  }, []);

  // Fetch messages for selected conversation
  useEffect(() => {
    if (selectedConversation) {
      axios.get(`/conversations/${selectedConversation.conversationId}/messages?limit=${SESSION_LIMIT}&skip=0`)
        .then((res) => {
          setMessages(res.data.messages);
          setMsgSkip(res.data.messages.length);
          // Scroll to bottom after messages load
          setTimeout(() => {
            if (chatContainerRef.current) {
              chatContainerRef.current.scrollTop = chatContainerRef.current.scrollHeight;
            }
          }, 100);
        })
        .catch((err) => console.error("Error fetching messages:", err));
    }
  }, [selectedConversation]);

  // Lazy loading older messages when scrolled to top
  const handleScroll = (e) => {
    if (e.target.scrollTop === 0 && hasMoreMessages && selectedConversation) {
      axios.get(`/conversations/${selectedConversation.conversationId}/messages?limit=${SESSION_LIMIT}&skip=${msgSkip}`)
        .then((res) => {
          if (res.data.messages.length < SESSION_LIMIT) {
            setHasMoreMessages(false);
          }
          // Prepend older messages
          setMessages(prev => [...res.data.messages, ...prev]);
          setMsgSkip(prev => prev + res.data.messages.length);
        })
        .catch((err) => console.error("Error loading more messages:", err));
    }
  };

  // Handle sending a new message
  const handleSendMessage = () => {
    if (!newMessage.trim() || !selectedConversation) return;
    const msg = {
      _id: `temp-${Date.now()}`,
      conversationId: selectedConversation.conversationId,
      from: "me", // Replace with your sender identifier if needed
      timestamp: Math.floor(Date.now() / 1000),
      content: { text: newMessage }
    };
    // Update messages state locally
    setMessages(prev => [...prev, msg]);
    setNewMessage('');
    // Optionally, send this message to the backend
    axios.post('/sendMessage', { conversationId: selectedConversation.conversationId, message: newMessage })
      .then(res => console.log("Message sent", res.data))
      .catch(err => console.error("Error sending message:", err));
  };

  // When an emoji is clicked, append it to the message input
  const onEmojiClick = (emojiData, event) => {
    setNewMessage(prev => prev + emojiData.emoji);
  };

  // Optional: close emoji picker when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (emojiPickerRef.current && !emojiPickerRef.current.contains(event.target)) {
        setShowEmojiPicker(false);
      }
    };
    if (showEmojiPicker) {
      document.addEventListener('mousedown', handleClickOutside);
    } else {
      document.removeEventListener('mousedown', handleClickOutside);
    }
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [showEmojiPicker]);

  return (
    <Grid container className="whatsapp-container">
      {/* Left Sidebar: Conversation List */}
      <Grid item xs={3} className="sidebar">
        <Paper className="sidebar-paper">
          <div className="sidebar-header">
            <div className="profile">
            </div>
            <div className="sidebar-header-icons">
              <FaSearch size={20} />
            </div>
          </div>
          <div className="sidebar-search">
            <div className="search-box">
              <FaSearch size={18} color="#999" />
              <input type="text"/>
            </div>
          </div>
          <div className="chat-list">
            {conversations.map((conv) => (
              <div
                key={conv.conversationId}
                className={`chat-list-item ${selectedConversation && conv.conversationId === selectedConversation.conversationId ? 'active' : ''}`}
                onClick={() => setSelectedConversation(conv)}
              >
                {conv.participants[0]?.avatar ? (
                  <img className="chat-avatar" src={conv.participants[0].avatar} alt="Avatar" />
                ) : (
                  <AccountCircleIcon className="chat-avatar" style={{ fontSize: 50 }} />
                )}
                <div className="chat-info">
                  <div className="chat-name-time">
                    <Typography variant="subtitle1" className="chat-name">
                      {conv.participants[0]?.name || conv.conversationId}
                    </Typography>
                    <Typography variant="caption" className="chat-time">
                      {new Date(conv.lastMessageAt).toLocaleTimeString()}
                    </Typography>
                  </div>
                  <Typography variant="body2" className="chat-last-message">
                    {conv.lastMessage ? conv.lastMessage : "No messages yet"}
                  </Typography>
                </div>
              </div>
            ))}
          </div>
        </Paper>
      </Grid>

      {/* Right Chat Area */}
      <Grid 
        item 
        xs={9} 
        className="chat-area" 
        sx={{ position: 'relative', height: '100%' }}
      >
        {selectedConversation ? (
          <Grid container direction="column" wrap="nowrap" sx={{ height: '100%' }}>
            {/* Chat Header */}
            <Grid item className="chat-header" sx={{ flexShrink: 0 }}>
              <Grid container alignItems="center" justifyContent="space-between">
                <Grid item>
                  <Grid container alignItems="center" className="chat-header-left">
                    {selectedConversation.participants[0]?.avatar ? (
                      <img
                        className="chat-avatar"
                        src={selectedConversation.participants[0].avatar}
                        alt="Avatar"
                        style={{ width: 40, height: 40, borderRadius: '50%', marginRight: 8 }}
                      />
                    ) : (
                      <AccountCircleIcon className="chat-avatar" style={{ fontSize: 40, marginRight: 8 }} />
                    )}
                    <div>
                      <Typography variant="h6">
                        {selectedConversation.participants[0]?.name || "Chat"}
                      </Typography>
                    </div>
                  </Grid>
                </Grid>
                <Grid item className="chat-header-right">
                  <FaSearch size={20} style={{ marginRight: 20 }} />
                </Grid>
              </Grid>
            </Grid>

            {/* Chat Messages */}
            <Grid
              item
              xs
              className="chat-messages"
              sx={{ overflowY: 'auto', p: 2 }}
              ref={chatContainerRef}
              onScroll={handleScroll}
            >
              {messages.map((msg) => (
                <div
                  key={msg._id}
                  className={`message-bubble ${msg.from === "me" ? 'sent' : 'received'}`}
                >
                  <Typography variant="body1">{msg.content.text}</Typography>
                  <Typography variant="caption" className="message-time">
                    {new Date(msg.timestamp * 1000).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                  </Typography>
                </div>
              ))}
            </Grid>

            {/* Chat Footer */}
            <Grid item className="chat-footer" sx={{ flexShrink: 0, p: 2 }}>
              <Grid container alignItems="center">
                <Grid item className="footer-icons" sx={{ mr: 2 }}>
                  <Button onClick={() => setShowEmojiPicker(prev => !prev)} style={{ minWidth: 'auto' }}>
                    <FaSmile size={24} style={{ marginRight: 15 }} color="success" />
                  </Button>
                </Grid>
                <Grid item xs>
                  <TextField
                    fullWidth
                    multiline
                    rows={1}
                    maxRows={4}  // optional, to cap how many lines it can grow
                    placeholder="Type a message"
                    value={newMessage}
                    onChange={(e) => setNewMessage(e.target.value)}
                    sx={{
                      backgroundColor: '#fff'
                    }}
                  />
                </Grid>
                <Grid item>
                  <Button onClick={handleSendMessage} color="success">
                    <SendIcon />
                  </Button>
                </Grid>
              </Grid>
            </Grid>

            {/* Emoji Picker container placed AFTER the footer but STILL inside .chat-area */}
            {showEmojiPicker && (
              <div className="emoji-picker-container" ref={emojiPickerRef}>
                <EmojiPicker onEmojiClick={onEmojiClick} />
              </div>
            )}
          </Grid>
        ) : (
          <div className="no-chat-selected">
            <Typography variant="h5">Select a chat to start messaging</Typography>
          </div>
        )}
      </Grid>
    </Grid>
  );
}
